<template>
  <div
    id="profile"
    class="flex flex-col gap-6 proxima_nova text_color overflow-y-scroll pb-4"
    style="max-height: calc(100vh - 104px)"
  >
    <h1 class="text-xl text_color font-semibold">Availability</h1>

    <div class="flex flex-col gap-12">
      <CallAvailability />

      <div
        id="meeting_availability_timings"
        class="flex flex-col gap-6 text-[13px] font-normal"
      >
        <span class="text-base font-semibold text_color"
          >Meeting availability timings</span
        >
        <div class="flex flex-col gap-6">
          <div
            v-for="(day, index) in daysOfWeeks"
            :key="`${day}_day_${index}`"
            class="flex flex-row gap-2 items-start"
          >
            <div class="flex flex-row gap-2 items-center w-40">
              <a-checkbox
                :checked="!availabilityForm.timeSlots[day].unavailable"
                @change="(e) => toggleDayAvailability(day, e.target.checked)"
              />
              <span>{{ day }}</span>
            </div>
            <span
              v-if="availabilityForm.timeSlots[day].unavailable"
              class="secondary_color text-sm"
              >Not available</span
            >
            <div v-else class="flex flex-col gap-2">
              <div
                v-for="(slot, index) in availabilityForm.timeSlots[day].slots"
                :key="`${day}_slot_${index}`"
                class="flex flex-row gap-2 items-center"
              >
                <a-select
                  v-model:value="slot.start"
                  showSearch
                  class="w-[112px] cursor-pointer"
                >
                  <template #suffixIcon>
                    <ThemeImage
                      :icon="imageNames.BOTTOM_ARROW"
                      alt="bottom_white_arrow"
                      class="size-4"
                    />
                  </template>
                  <a-select-option
                    v-for="(time, index) in timeIntervals"
                    :key="`${time}_time_${index}`"
                    :value="time"
                  >
                  </a-select-option>
                </a-select>
                <a-select
                  v-model:value="slot.end"
                  showSearch
                  class="w-[112px] cursor-pointer"
                >
                  <template #suffixIcon>
                    <ThemeImage
                      :icon="imageNames.BOTTOM_ARROW"
                      alt="bottom_white_arrow"
                      class="size-4"
                    />
                  </template>
                  <a-select-option
                    v-for="(time, index) in timeIntervals"
                    :key="`${time}_time_${index}`"
                    :value="time"
                  >
                  </a-select-option>
                </a-select>
                <div class="flex flex-row items-center mx-1">
                  <div
                    class="cursor-pointer m-1 p-1"
                    @click="() => handleRemoveSlot(day, index)"
                  >
                    <ThemeImage
                      :icon="imageNames.CLOSE_GREY"
                      alt="cross_icon"
                      class="size-[14px]"
                    />
                  </div>
                  <div
                    v-if="index === 0"
                    class="cursor-pointer mx-2 my-1 p-1"
                    @click="() => handleAddSlot(day)"
                  >
                    <ThemeImage
                      :icon="imageNames.ADD_ROUNDED_GREY"
                      alt="cross_icon"
                      class="size-[14px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div id="timezone" class="form_col">
            <span>Timezone</span>
            <a-select
              v-model:value="availabilityForm.timezone"
              showSearch
              :show-arrow="false"
              :filter-option="filterDropdown"
              class="w-[400px]"
            >
              <template #suffixIcon
                ><ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
              /></template>
              <a-select-option
                v-for="timezone in timeZones"
                :key="`timezone_${timezone}`"
                :value="timezone"
                :label="timezone.replace('_', ' ')"
              >
                {{ timezone.replace("_", " ") }}
              </a-select-option>
            </a-select>
          </div>
          <div id="default_link" class="form_col">
            <span>Default Personal Meeting Link</span>
            <a-select
              v-model:value="availabilityForm.agent_link"
              showSearch
              :filter-option="filterDropdown"
              placeholder="Select Meeting Agent"
              class="w-[400px]"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="agent in oneOnOneMeetingAgents"
                :key="`agent_${agent.id}`"
                :value="agent.public_link"
              >
                {{ agent.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>
      </div>
    </div>

    <a-button
      @click="onSubmit"
      class="primary_medium w-fit"
      :loading="isLoading"
    >
      Update changes
    </a-button>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { VueTelInput } from "vue-tel-input";
import DashboardService from "@/src/api/DashboardService.js";
import eventBus from "@/src/utils/eventBus.js";
import { timeZones } from "@/src/constants/timeZoneConstants.js";

import {
  convertTo12Hour,
  convertTo24HourFormat,
  deepCopy,
  filterDropdown,
  generateTimeOptions,
  getTimeZone,
} from "@/src/utils/utils.js";
import {
  daysOfWeeks,
  defaultTimeSlots,
} from "@/src/constants/meetingAgentConstants.js";
import dashboardService from "@/src/api/DashboardService.js";
import CallAvailability from "@/src/views/availability/CallAvailability.vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const timeIntervals = generateTimeOptions();
const isLoading = ref(false);
const meetingAgents = ref([]);

const createInitialFormState = () => ({
  timeSlots: deepCopy(defaultTimeSlots),
  timezone: getTimeZone(),
  agent_link: null,
});
const availabilityForm = reactive(createInitialFormState());

const toggleDayAvailability = (day, isAvailable) => {
  if (isAvailable) {
    availabilityForm.timeSlots[day] = {
      slots: [{ start: "09:00am", end: "5:00pm" }],
    };
  } else {
    availabilityForm.timeSlots[day] = { unavailable: true };
  }
};

const handleRemoveSlot = (day, index) => {
  availabilityForm.timeSlots[day].slots.splice(index, 1);
  if (availabilityForm.timeSlots[day].slots.length === 0) {
    availabilityForm.timeSlots[day] = { unavailable: true };
  }
};

const handleAddSlot = (day) => {
  if (availabilityForm.timeSlots[day].unavailable) {
    availabilityForm.timeSlots[day] = { slots: [] };
  }
  availabilityForm.timeSlots[day].slots.push({
    start: "09:00am",
    end: "5:00pm",
  });
};

const oneOnOneMeetingAgents = computed(() => {
  meetingAgents.value = meetingAgents.value.filter(
    (agent) => agent.type === "one_on_one",
  );
  return meetingAgents.value;
});

const updateDefaultMeetingLink = async () => {
  try {
    if (!availabilityForm.agent_link) {
      message.error("No meeting agent selected");
      return;
    }

    const payload = {
      agent_link: availabilityForm.agent_link,
    };

    const response = await dashboardService.updateDefaultMeetingLink(payload);
    if (response.success) {
      message.success("Default Meeting Link updated successfully");
    } else {
      message.error("Failed to update default link");
    }
  } catch (error) {
    message.error("An error occurred while updating default link in settings");
  }
};

const onSubmit = async () => {
  isLoading.value = true;
  try {
    await updateDefaultMeetingLink();

    const payload = {
      time_slots: convertTo24HourFormat(availabilityForm.timeSlots),
      timezone: availabilityForm.timezone,
    };

    const response = await dashboardService.updateUserAvailability(payload);

    if (response.success) {
      message.success("Availability settings updated successfully");
    } else {
      message.error("Failed to update availability settings");
    }
  } catch (error) {
    console.error("Error updating availability:", error);
    message.error("An error occurred while updating availability settings");
  } finally {
    isLoading.value = false;
  }
};

const fetchMeetingAgents = async () => {
  try {
    const response = await DashboardService.fetchMeetingAgents({
      page_size: -1,
    });
    meetingAgents.value = response.agents;
  } catch (err) {
    meetingAgents.value = [];
  }
};

const fetchUserAvailability = async () => {
  try {
    const data = await dashboardService.getUserAvailability();
    if (data?.availability) {
      const normalizedTimeSlots = {};

      Object.entries(data.availability).forEach(([day, dayData]) => {
        if (dayData.unavailable) {
          normalizedTimeSlots[day] = { unavailable: true };
        } else if (dayData.slots) {
          normalizedTimeSlots[day] = {
            slots: dayData.slots.map((slot) => ({
              start: convertTo12Hour(slot.start.split("+")[0]),
              end: convertTo12Hour(slot.end.split("+")[0]),
            })),
          };
        }
      });

      availabilityForm.timeSlots = normalizedTimeSlots;
    }
    if (data?.timezone) {
      availabilityForm.timezone = data.timezone;
    }
    if (data?.personal_meeting_link) {
      availabilityForm.agent_link = data.personal_meeting_link;
    }
  } catch (error) {
    console.error("Error fetching availability:", error);
    message.error("Failed to load availability settings");
  }
};

onMounted(async () => {
  await fetchUserAvailability();
  await fetchMeetingAgents();
});
</script>
